import { AddIcon, DeleteIcon } from '@chakra-ui/icons'
import {
  Box,
  BoxProps,
  Button,
  Center,
  Checkbox,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  VStack
} from '@chakra-ui/react'
import React, { useRef } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useDebounce } from 'react-use'
import { FormNumberInput } from 'src/components/common/Forms/FormNumberInput'
import { FormPriceAmountInput } from 'src/components/common/Forms/FormPriceAmountInput'
import { FormSelect } from 'src/components/common/Forms/FormSelect'
import { FormTextInput } from 'src/components/common/Forms/FormTextInput'
import { RequiredIndicator } from 'src/components/common/RequiredIndicator'
import { VFlex } from 'src/components/common/VFlex'
import { useCalculatedAmounts } from 'src/hooks/useCalculatedAmounts'
import { AccountItem } from 'src/interfaces/api/GetAccountItems'
import { Tax } from 'src/interfaces/api/GetTaxes'
import { FormLabelTheme } from 'src/styles/components/formLabel'
import { FormInvoiceItem, InvoiceFormInputs } from '../InvoiceEditForm'
import InvoiceEditorSection from '../InvoiceEditorSection'
import { AccountItemSelectMenu } from './AccountItemSelectMenu'
import { TaxSettingModal } from './TaxSettingModal'

const TableLabel: React.FC<BoxProps> = ({ children, ...props }) => {
  return (
    <Box
      sx={{
        ...FormLabelTheme.baseStyle,
        lineHeight: '18px',
        fontSize: '11px',
        m: 0
      }}
      {...props}
    >
      {children}
    </Box>
  )
}

type Props = {
  accountItems: AccountItem[]
  taxes: Tax[]
}

export const InvoiceReceiptInformation = ({ accountItems, taxes }: Props) => {
  const { control, watch, getValues } = useFormContext<InvoiceFormInputs>()
  const { fields, append, remove, update } = useFieldArray<InvoiceFormInputs>({
    control,
    name: 'items'
  })

  const initialInvoiceItem: FormInvoiceItem = {
    name: '',
    quantity: 1,
    unit: '',
    unit_price: 0,
    tax_id: taxes[0].id,
    is_withheld: false,
    account_item_id: accountItems[0].id,
    id: Date.now()
  }

  const isUnitShown = watch('taxSettings.unit') === '1'
  const isWithholdingShown = watch('taxSettings.withholding') === '1'

  const { calculated_amount, mutateCalculatedAmount } = useCalculatedAmounts(
    watch('items'),
    watch('taxSettings')
  )
  const {
    calculated_amount: {
      tax_amount_of_tax_rate_10,
      tax_amount_of_tax_rate_8,
      total_amount,
      withholding_amount
    },
    invoice_items
  } = calculated_amount
  // お金の情報のうち、金額計算に必要な値を連結したハッシュ値のようなものを作る関数
  const prevRef = useRef('')
  const hash = watch('items').reduce((prev, curr) => {
    return (
      prev + curr.unit_price + curr.quantity + curr.tax_id + curr.is_withheld
    )
  }, '')
  useDebounce(
    () => {
      hash !== prevRef.current && mutateCalculatedAmount()
      prevRef.current = hash
    },
    500,
    [hash, mutateCalculatedAmount]
  )
  return (
    <InvoiceEditorSection
      title="お金の情報"
      number={4}
      button={<TaxSettingModal />}
    >
      <Flex w="100%" gap="8px" align="center" maxW={'950px'}>
        <TableLabel w="50px">カテゴリ</TableLabel>
        <TableLabel flex={1} display="flex" pos="relative">
          <Text textAlign="left">項目名</Text>
          <RequiredIndicator left="31px" d="inline-block" />
        </TableLabel>
        <TableLabel w="80px" textAlign="left">
          単価
        </TableLabel>
        <TableLabel w="38px" textAlign="left">
          数量
        </TableLabel>
        {isUnitShown && (
          <TableLabel w="30px" textAlign="left">
            単位
          </TableLabel>
        )}
        <TableLabel w="75px" textAlign="left">
          消費税
        </TableLabel>

        {isWithholdingShown && <TableLabel w="22px">源泉</TableLabel>}
        <TableLabel w="68px" textAlign="right">
          金額
        </TableLabel>
      </Flex>
      <VFlex gap="16px" align="stretch" mt="8px" maxW={'950px'}>
        {fields.map((v, i) => (
          <Popover key={i} trigger="hover" placement="left" closeDelay={200}>
            <PopoverTrigger>
              <Flex gap="8px" align="center" alignItems={'flex-start'} h="44px">
                <Box w="50px">
                  <AccountItemSelectMenu
                    accountItems={accountItems}
                    selectedAccountItemId={v.account_item_id}
                    onClick={(id) => {
                      update(i, {
                        ...getValues().items[i],
                        account_item_id: id
                      })
                    }}
                  />
                </Box>
                <Box flex={1}>
                  <FormTextInput<InvoiceFormInputs>
                    name={`items.${i}.name` as const}
                    w="100%"
                    placeholder="例）業務委託費用"
                    isRequired
                  />
                </Box>
                <FormPriceAmountInput<InvoiceFormInputs>
                  name={`items.${i}.unit_price` as const}
                  w="80px"
                  isRequired
                />
                <FormNumberInput<InvoiceFormInputs>
                  name={`items.${i}.quantity` as const}
                  w="38px"
                  isRequired
                />

                {isUnitShown && (
                  <FormTextInput<InvoiceFormInputs>
                    name={`items.${i}.unit` as const}
                    w="30px"
                  />
                )}
                <FormSelect<InvoiceFormInputs, number>
                  valueAsNumber
                  name={`items.${i}.tax_id` as const}
                  w="75px"
                >
                  {taxes.map((tax, i) => (
                    <option key={`taxes-key-${i}`} value={tax.id}>
                      {tax.abbreviated_name}
                    </option>
                  ))}
                </FormSelect>

                {isWithholdingShown && (
                  <Center w="22px" h="100%">
                    <Checkbox
                      size="xl"
                      isChecked={v.is_withheld}
                      onChange={(e) => {
                        update(i, {
                          ...getValues().items[i],
                          is_withheld: e.target.checked
                        })
                      }}
                    />
                  </Center>
                )}

                <Box w="68px" height={'100%'}>
                  <Flex
                    alignItems={'center'}
                    justifyContent={'flex-end'}
                    height={'100%'}
                  >
                    {invoice_items?.length > 0 && (
                      <Text fontSize={'13px'} isTruncated>
                        ¥{invoice_items[i]?.amount?.toLocaleString()}
                      </Text>
                    )}
                  </Flex>
                </Box>
              </Flex>
            </PopoverTrigger>
            {fields.length > 1 && (
              <PopoverContent bgColor="white.100" w="auto">
                <IconButton
                  variant="ghost"
                  aria-label="削除"
                  color="gray.800"
                  onClick={() => remove(i)}
                  icon={<DeleteIcon />}
                />
              </PopoverContent>
            )}
          </Popover>
        ))}
      </VFlex>
      <HStack
        justifyContent={'space-between'}
        alignItems={'flex-start'}
        width={'100%'}
        maxW={'950px'}
        mt="16px"
      >
        <Button
          variant="text"
          color="primary.purple"
          leftIcon={<AddIcon fontSize="10px" />}
          padding={0}
          fontSize={'13px'}
          minH="13px"
          h="13px"
          onClick={() => {
            if (fields.length > 27) {
              alert('「お金の情報」は27行まで登録可能です。')
              return
            }
            append(initialInvoiceItem)
          }}
        >
          行を追加
        </Button>
        <VStack
          align="stretch"
          w="204px"
          spacing="12px"
          fontSize="13px"
          lineHeight={1}
          fontWeight={400}
        >
          <Flex justify="space-between">
            <Text>小計</Text>
            <Text>
              ¥
              {invoice_items
                ? invoice_items
                    .reduce((previous, current) => {
                      return (previous = previous + current['amount'])
                    }, 0)
                    .toLocaleString()
                : 0}
            </Text>
          </Flex>
          <Flex justify="space-between">
            <Text>消費税</Text>
            <Text>
              ¥
              {calculated_amount
                ? (
                    tax_amount_of_tax_rate_10 + tax_amount_of_tax_rate_8
                  ).toLocaleString()
                : 0}
            </Text>
          </Flex>
          <Flex justify="space-between">
            <Text> 源泉徴収税</Text>
            <Text>
              ¥
              {calculated_amount &&
              calculated_amount.calculated_amount.withholding_amount > 0
                ? `-${withholding_amount.toLocaleString()}`
                : 0}
            </Text>
          </Flex>
          <Flex justify="space-between" fontWeight={700}>
            <Text>合計</Text>
            <Text>
              ¥{calculated_amount ? total_amount.toLocaleString() : 0}
            </Text>
          </Flex>
        </VStack>
      </HStack>
    </InvoiceEditorSection>
  )
}
